import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowRight, CheckCircle, Clock, XCircle } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api, APIError } from '../../api/client'
import { useB2BBillingStore } from '../../stores/useB2BBillingStore'

/**
 * After Stripe returns the user to /b2b/payment-result, we poll
 * `/api/b2b/billing/payment-status/{order_reference}` until the webhook
 * marks the payment as approved/declined/expired.
 */
export default function B2BPaymentResultScreen() {
  const location = useLocation()
  const navigate = useNavigate()
  const loadSubscription = useB2BBillingStore((s) => s.loadSubscription)

  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const subscriptionRefreshedRef = useRef(false)

  const orderReference = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return (
      params.get('order_reference') ||
      params.get('orderReference') ||
      params.get('order') ||
      (typeof window !== 'undefined' ? localStorage.getItem('lastB2BPaymentOrderReference') : null)
    )
  }, [location.search])

  const fetchStatus = useCallback(async () => {
    if (!orderReference) {
      setError('Order reference not found.')
      setLoading(false)
      return
    }
    try {
      const data = await api.get(`/api/b2b/billing/payment-status/${orderReference}`)
      setStatus(data)
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not load payment status.')
    } finally {
      setLoading(false)
    }
  }, [orderReference])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  // Poll while still in 'created' state (webhook may arrive a few seconds late).
  useEffect(() => {
    if (!status || status.status !== 'created') return
    const id = setTimeout(() => { fetchStatus() }, 3000)
    return () => clearTimeout(id)
  }, [status, fetchStatus])

  // When approved, refresh subscription store so /b2b/billing reflects the new plan.
  useEffect(() => {
    if (status?.status === 'approved' && !subscriptionRefreshedRef.current) {
      subscriptionRefreshedRef.current = true
      loadSubscription()
    }
  }, [status, loadSubscription])

  const cfg = useMemo(() => statusConfig(status), [status])

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-2xl mx-auto">
        {loading && !status && (
          <div className="bg-white rounded-3xl border border-black/5 p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent mx-auto mb-4" />
            <p className="text-sm text-ink-3">Checking payment status…</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-white rounded-3xl border border-black/5 p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-ink-1 mb-2">Error</h1>
            <p className="text-sm text-ink-3">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/b2b/pricing')}
              className="mt-6 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl"
            >
              Back to pricing
            </button>
          </div>
        )}

        {status && cfg && (
          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-black/5 p-8 text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${cfg.bg}`}>
                <cfg.Icon className={`w-8 h-8 ${cfg.icon}`} />
              </div>
              <h1 className="text-2xl font-bold text-ink-1 mb-1">{cfg.title}</h1>
              <p className="text-sm text-ink-3">{cfg.message || (status.reason || '')}</p>

              <div className="mt-5 inline-block rounded-2xl border border-black/5 bg-canvas px-4 py-2 text-xs font-mono text-ink-2">
                Order: {status.order_reference}
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-black/5 p-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Cell label="Plan" value={status.plan_code} />
              <Cell label="Amount" value={`$${Number(status.amount).toFixed(2)} ${status.currency}`} />
              <Cell label="Created" value={fmtDate(status.created_at)} />
            </div>

            <div className="flex flex-wrap gap-3 justify-center">
              {status.status === 'approved' ? (
                <Link
                  to="/b2b/billing"
                  className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl"
                >
                  Go to billing
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <Link
                  to="/b2b/pricing"
                  className="inline-flex items-center gap-2 border border-black/10 bg-white hover:bg-canvas text-ink-1 font-bold px-5 py-2.5 rounded-2xl"
                >
                  Back to pricing
                </Link>
              )}
              {status.status === 'created' && (
                <button
                  type="button"
                  onClick={fetchStatus}
                  className="inline-flex items-center gap-2 border border-black/10 bg-white hover:bg-canvas text-ink-1 font-bold px-5 py-2.5 rounded-2xl"
                >
                  Refresh
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}

function statusConfig(status) {
  if (!status) return null
  const value = status.status
  if (value === 'approved') {
    return { Icon: CheckCircle, bg: 'bg-emerald-50', icon: 'text-emerald-500', title: 'Payment successful', message: 'Your B2B subscription is now active.' }
  }
  if (value === 'declined') {
    return { Icon: XCircle, bg: 'bg-red-50', icon: 'text-red-500', title: 'Payment declined', message: status.reason || 'Your payment was not processed.' }
  }
  if (value === 'expired') {
    return { Icon: AlertCircle, bg: 'bg-amber-50', icon: 'text-amber-500', title: 'Payment expired', message: 'The payment session expired before completion.' }
  }
  return { Icon: Clock, bg: 'bg-amber-50', icon: 'text-amber-500', title: 'Payment pending', message: 'Waiting for confirmation from the payment provider…' }
}

function Cell({ label, value }) {
  return (
    <div className="rounded-2xl bg-canvas px-4 py-3">
      <p className="text-xs font-bold uppercase tracking-wider text-ink-3">{label}</p>
      <p className="mt-1 text-sm font-semibold text-ink-1 break-words">{value}</p>
    </div>
  )
}

function fmtDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}
